/* Admin · Ingestion & HIL Review — the prototype's single master-detail page.
 *
 * Left: the review queue. Right: the selected review — source page vs extracted text
 * toggle, chunk preview, reviewer note, and the Approve / Reject decision. Below: documents
 * in flight and the review decision log, both from the same real endpoints.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'

import { admin, hil } from '../../api/client'
import type { DocumentSummary, HilReviewDetail, HilReviewSummary } from '../../api/types'
import {
  Card,
  EmptyState,
  Kpi,
  PageHead,
  Skeleton,
  dateHuman,
  errorMessage,
  nf,
  useConfirm,
  useModal,
  useToast,
} from '../../ui/chrome'
import { Icon } from '../../ui/icons'
import { exportCSV } from '../../ui/pdf'
import { statusPill } from './DocumentsScreen'

/** "rajesh.nair@kirloskar.com" → "Rajesh Nair". The UPN stays available in the tooltip. */
function reviewerName(upn: string): string {
  const local = upn.split('@')[0] ?? upn
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

function slaPill(r: HilReviewSummary) {
  const label = r.age_hours < 1 ? `${Math.round(r.age_hours * 60)}m` : `${Math.floor(r.age_hours)}h`
  if (r.is_overdue) return <span className="pill p-danger">SLA breached · {label} in queue</span>
  if (r.sla_due_at && r.age_hours > 5) return <span className="pill p-warn">{label} in queue</span>
  return <span className="pill p-ok">{label} in queue</span>
}

export default function IngestScreen({ onChanged }: { onChanged: () => void }) {
  const toast = useToast()
  const confirm = useConfirm()
  const { openModal, closeModal } = useModal()

  const [queue, setQueue] = useState<HilReviewSummary[] | null>(null)
  const [pendingCount, setPendingCount] = useState(0)
  const [overdueCount, setOverdueCount] = useState(0)
  const [decided, setDecided] = useState<HilReviewSummary[]>([])
  const [inFlight, setInFlight] = useState<DocumentSummary[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [detail, setDetail] = useState<HilReviewDetail | null>(null)
  const [view, setView] = useState<'scan' | 'text'>('scan')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(() => {
    Promise.all([
      hil.listReviews({ status: 'queued' }),
      hil.listReviews({ status: 'in_review' }),
    ])
      .then(([queued, inReview]) => {
        const items = [...inReview.items, ...queued.items]
        setQueue(items)
        setPendingCount(queued.pending_count)
        setOverdueCount(queued.overdue_count)
        setSelectedId((current) => current ?? items[0]?.id ?? null)
        setError(null)
      })
      .catch((cause: unknown) => setError(errorMessage(cause)))

    Promise.all([
      hil.listReviews({ status: 'approved' }),
      hil.listReviews({ status: 'rejected' }),
    ])
      .then(([approved, rejected]) => {
        setDecided(
          [...approved.items, ...rejected.items].sort((a, b) =>
            b.created_at.localeCompare(a.created_at),
          ),
        )
      })
      .catch(() => setDecided([]))

    admin
      .documents({ limit: 200 })
      .then((response) =>
        setInFlight(
          response.items.filter((d) =>
            ['discovered', 'fetching', 'extracting', 'ocr', 'chunking', 'pending_review', 'failed'].includes(
              d.status,
            ),
          ),
        ),
      )
      .catch(() => setInFlight([]))
  }, [])

  useEffect(refresh, [refresh])

  useEffect(() => {
    if (!selectedId) {
      setDetail(null)
      return
    }
    setDetail(null)
    setNote('')
    hil
      .getReview(selectedId)
      .then(setDetail)
      .catch((cause: unknown) => toast('err', 'Review could not be loaded', errorMessage(cause)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId])

  const oldest = useMemo(() => {
    if (!queue || queue.length === 0) return null
    return queue.reduce((a, b) => (a.age_hours > b.age_hours ? a : b))
  }, [queue])

  /** Resolves once the server has accepted the decision, so the caller can only announce
   *  success after there is one. Announcing it alongside the request produced a "published"
   *  and a "failed" toast for the same click. */
  function decide(review: HilReviewDetail, decision: 'approve' | 'reject', extra: { notes?: string }) {
    return hil
      .decide(review.id, {
        decision,
        notes: extra.notes,
      })
      .then(() => {
        setSelectedId(null)
        refresh()
        onChanged()
        return true
      })
      .catch((cause: unknown) => {
        toast('err', 'Decision failed', errorMessage(cause))
        return false
      })
  }

  function approve(review: HilReviewDetail) {
    confirm(
      'Approve and publish?',
      `“${review.document_name}” becomes searchable for everyone its access groups allow.`,
      'Approve & publish',
      () => {
        void decide(review, 'approve', { notes: note || 'Extraction validated' }).then((ok) => {
          if (ok) {
            toast(
              'ok',
              'Document published',
              `${review.document_name} is searchable once indexing completes.`,
            )
          }
        })
      },
    )
  }

  function reindex(document: DocumentSummary) {
    confirm(
      'Re-index this document?',
      `“${document.name}” will be re-extracted, re-chunked and re-embedded from its source file, replacing its existing chunks.` +
        (document.status === 'indexed'
          ? ' It returns to the review queue and is not searchable until a reviewer approves it again.'
          : ''),
      'Re-index',
      () => {
        admin
          .reindex(document.id)
          .then(() => {
            // No "queued" branch: the endpoint either starts the work and returns running, or
            // refuses with an error explaining why. A toast for a state that cannot occur is
            // a promise the code does not keep.
            toast(
              'ok',
              'Re-indexing started',
              `${document.name} — progress appears in this table.`,
            )
            refresh()
          })
          .catch((cause: unknown) => toast('err', 'Re-index failed', errorMessage(cause)))
      },
    )
  }

  function reject(review: HilReviewDetail) {
    let reason = note
    openModal({
      title: 'Reject document',
      sub: review.document_name,
      size: 'narrow',
      body: (
        <div className="field">
          <label htmlFor="hrNote">
            Reason for rejection <span className="req">*</span>
          </label>
          <textarea
            id="hrNote"
            className="inp"
            defaultValue={note}
            placeholder="Why can this not be published?"
            onChange={(event) => {
              reason = event.target.value
            }}
          />
        </div>
      ),
      footer: (
        <>
          <button type="button" className="btn btn-soft" onClick={closeModal}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => {
              if (reason.trim().length < 8) {
                toast('err', 'Reason required', 'Describe the rejection in at least 8 characters.')
                return
              }
              closeModal()
              void decide(review, 'reject', { notes: reason }).then((ok) => {
                if (ok) {
                  toast(
                    'ok',
                    'Document rejected',
                    `${review.document_name} will not enter the index.`,
                  )
                }
              })
            }}
          >
            Reject document
          </button>
        </>
      ),
    })
  }

  const ocrPct = detail?.extraction_confidence !== null && detail?.extraction_confidence !== undefined
    ? Math.round(detail.extraction_confidence * 1000) / 10
    : null

  return (
    <>
      <PageHead
        title="Ingestion & HIL Review"
        actions={
          <button type="button" className="btn btn-lime" onClick={refresh}>
            <Icon name="refresh" size={16} /> Refresh queue
          </button>
        }
      />

      <div className="grid g4 mb16">
        <Kpi
          tone="info"
          label="In processing"
          value={nf(inFlight.filter((d) => ['fetching', 'extracting', 'ocr', 'chunking'].includes(d.status)).length)}
          hoverDesc="Being read and prepared"
        />
        <Kpi tone="warn" label="Awaiting review" value={nf(pendingCount)} hoverDesc="Waiting for a reviewer's check" />
        <Kpi
          tone={overdueCount > 0 ? 'danger' : 'green'}
          label="Oldest in queue"
          value={
            oldest ? (
              <span style={{ fontSize: 22 }}>
                {oldest.age_hours < 1 ? `${Math.round(oldest.age_hours * 60)}m` : `${Math.floor(oldest.age_hours)}h`}
              </span>
            ) : (
              '—'
            )
          }
          hoverDesc={
            overdueCount > 0 ? `${nf(overdueCount)} waiting longer than expected` : 'Nothing is overdue'
          }
        />
        <Kpi
          tone="danger"
          label="Failed"
          value={nf(inFlight.filter((d) => d.status === 'failed').length)}
          hoverDesc="Something went wrong. Scan these again"
        />
      </div>

      {error && (
        <div className="alert a-danger">
          <Icon name="alert" size={18} />
          <div>
            <strong>The review queue could not be loaded.</strong>
            {error}
          </div>
        </div>
      )}

      <div className="grid g-1-2 mb16">
        <Card icon="clipboard" title="Review queue" hint={queue ? String(queue.length) : undefined} tight>
          {queue === null ? (
            <Skeleton rows={4} />
          ) : queue.length === 0 ? (
            <EmptyState
              icon="checkCircle"
              title="Queue is clear"
              msg="Every ingested document has been reviewed."
            />
          ) : (
            queue.map((r) => (
              <div
                key={r.id}
                className="link-row"
                role="button"
                tabIndex={0}
                style={{
                  borderBottom: '1px solid var(--line-2)',
                  background: r.id === selectedId ? 'var(--lime-lt)' : undefined,
                  cursor: 'pointer',
                  padding: '12px 14px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 10,
                }}
                onClick={() => setSelectedId(r.id)}
                onKeyDown={(event) => event.key === 'Enter' && setSelectedId(r.id)}
              >
                <div style={{ minWidth: 0 }}>
                  <div className="lr-t">{r.document_name}</div>
                  <div className="lr-d">
                    {r.extraction_type === 'scanned' ? 'Scanned (OCR)' : 'Digital'}
                  </div>
                  <div className="tiny muted mt8">
                    {r.assigned_to ? `Assigned to ${r.assigned_to}` : 'Unassigned'}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
                  {r.extraction_confidence !== null && r.extraction_confidence < 0.9 ? (
                    <span className="pill p-warn">Low OCR</span>
                  ) : (
                    <span className="pill p-info">Pending</span>
                  )}
                  {slaPill(r)}
                </div>
              </div>
            ))
          )}
        </Card>

        <div>
          {!selectedId || !detail ? (
            <Card icon="doc" title="Review">
              {selectedId ? (
                <Skeleton rows={5} />
              ) : (
                <EmptyState
                  icon="checkCircle"
                  title="Nothing to review"
                  msg="Documents appear here after extraction and before they become searchable."
                />
              )}
            </Card>
          ) : (
            <>
              <Card
                icon="doc"
                title={detail.document_name}
                hint={`Detected ${dateHuman(detail.created_at)}`}
              >
                {ocrPct !== null && ocrPct < 90 && (
                  <div className="alert a-warn">
                    <Icon name="warn" size={18} />
                    <div>
                      <strong>OCR confidence {ocrPct}%</strong>
                      Check the extracted text against the source page before approving.
                    </div>
                  </div>
                )}

                <div className="flex between aic wrap gap8 mb12">
                  <div className="flex aic gap8">
                    <button
                      type="button"
                      className={`btn btn-xs ${view === 'scan' ? 'btn-lime' : 'btn-soft'}`}
                      onClick={() => setView('scan')}
                    >
                      <Icon name="eye" size={13} /> Page image
                    </button>
                    <button
                      type="button"
                      className={`btn btn-xs ${view === 'text' ? 'btn-lime' : 'btn-soft'}`}
                      onClick={() => setView('text')}
                    >
                      <Icon name="file" size={13} /> Extracted text
                    </button>
                  </div>
                  {slaPill(detail)}
                </div>

                <div className="hil-split">
                  <div className="hil-pane">
                    <div className="hp-h">
                      <span>{view === 'scan' ? 'Source page (as received)' : 'Source text (as extracted)'}</span>
                      {ocrPct !== null ? (
                        <span className={`conf ${ocrPct >= 90 ? 'hi' : ocrPct >= 70 ? 'md' : 'lo'}`}>
                          OCR {ocrPct}%
                        </span>
                      ) : (
                        <span className="conf hi">Digital</span>
                      )}
                    </div>
                    <div className={`hp-b${view === 'text' ? ' scan' : ''}`}>
                      {view === 'scan' ? (
                        /* The ORIGINAL page, or an honest statement that we do not have it.
                         *
                         * This pane used to fall back to a simulated scan: the KBL logo, a
                         * header, and the EXTRACTED TEXT laid out in a scanned-page style.
                         * On the one screen whose entire purpose is checking extraction
                         * against its source, that showed the reviewer the extraction twice
                         * and invited them to approve it as a match with itself. A reviewer
                         * cannot catch a bad transcription by reading the transcription. */
                        detail.source_page_url ? (
                          <iframe
                            title={`Page ${detail.current_page} of ${detail.document_name}`}
                            key={detail.source_page_url}
                            src={detail.source_page_url}
                            style={{ width: '100%', height: 520, border: 0 }}
                          />
                        ) : (
                          <div className="tiny muted" style={{ padding: 16 }}>
                            <strong>The original page is not available on this server.</strong>
                            <div className="mt8">
                              There is nothing to check the extracted text against here, so
                              approving this document means trusting the extraction rather
                              than verifying it. Documents ingested from SharePoint should be
                              opened at the source; for locally-ingested documents this means
                              the file is missing from the knowledge-base directory.
                            </div>
                          </div>
                        )
                      ) : (
                        <div style={{ whiteSpace: 'pre-wrap' }}>
                          {detail.page_text ?? 'No extracted text stored for this page.'}
                        </div>
                      )}
                    </div>
                  </div>

                </div>

                {detail.chunk_preview && (
                  <div className="card mt16" style={{ marginBottom: 0 }}>
                    <div className="card-h">
                      <h3>
                        <Icon name="layers" size={16} /> Chunks to be indexed
                        <span className="hint">
                          {detail.chunk_page_start !== null && detail.chunk_page_end !== null
                            ? `p.${detail.chunk_page_start}–${detail.chunk_page_end}`
                            : undefined}
                        </span>
                      </h3>
                    </div>
                    <div className="card-b">
                      <div className="chunk-row">
                        <span className="cr-s">
                          <Icon name="file" size={14} />
                        </span>
                        <span className="tiny">{detail.chunk_preview}</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="field mt16">
                  <label htmlFor="hilNote">Reviewer note</label>
                  <textarea
                    id="hilNote"
                    className="inp"
                    placeholder="Record what you checked, or why this is rejected"
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                  />
                </div>

                {/* Two decisions, not four. A reviewer either accepts what will be indexed or
                    they do not; "approve with edits" was the same approval with a different
                    label, and assignment is queue administration rather than a decision about
                    the document. */}
                <div className="flex wrap gap8 mt12">
                  <button type="button" className="btn btn-lime" onClick={() => approve(detail)}>
                    <Icon name="checkCircle" size={16} /> Approve &amp; publish
                  </button>
                  <button type="button" className="btn btn-danger" onClick={() => reject(detail)}>
                    <Icon name="x" size={16} /> Reject
                  </button>
                </div>
              </Card>
            </>
          )}
        </div>
      </div>

      <Card icon="cloud" title="Documents in flight" tight className="mb16">
        {inFlight.length === 0 ? (
          <EmptyState
            icon="checkCircle"
            title="Pipeline is clear"
            msg="Every detected document has been processed and indexed."
          />
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Document</th>
                  <th>Source</th>
                  <th>Stage</th>
                  <th>Progress</th>
                  {/* Nothing followed Progress, so a document sitting failed or stuck could be
                      watched but not acted on — the one thing an admin opens this table to do. */}
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {inFlight.map((d) => (
                  <tr key={d.id}>
                    <td>
                      <strong>{d.name}</strong>
                      <div className="tiny muted mono">
                        {d.source === 'sharepoint' ? 'SharePoint' : 'OneDrive'}
                        {d.source_label ? ` / ${d.source_label}` : ''}
                      </div>
                    </td>
                    <td>
                      <span className="tiny">{d.ocr_applied ? 'Scanned (OCR)' : 'Digital'}</span>
                    </td>
                    <td>{statusPill(d)}</td>
                    <td style={{ width: 170 }}>
                      <div className="progress">
                        <i
                          className={
                            d.status === 'failed' ? 'danger' : d.status === 'pending_review' ? 'warn' : undefined
                          }
                          style={{
                            width: `${
                              d.status === 'failed'
                                ? 100
                                : d.status === 'pending_review'
                                  ? 90
                                  : d.status === 'discovered'
                                    ? 4
                                    : d.progress_pct
                            }%`,
                          }}
                        />
                      </div>
                      <div className="tiny muted mt8">
                        {d.status === 'failed'
                          ? d.error_message ?? 'Error'
                          : d.status === 'pending_review'
                            ? 'Reviewer'
                            : d.status === 'discovered'
                              ? 'Waiting'
                              : (d.stage_detail ?? `${d.progress_pct}%`)}
                        {d.retry_count > 0 && (
                          <>
                            {' · '}
                            <span title="Ingestion has already been attempted this many times">
                              {d.retry_count} retr{d.retry_count === 1 ? 'y' : 'ies'}
                            </span>
                          </>
                        )}
                      </div>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        type="button"
                        className="btn-icon"
                        title="Re-run extraction, OCR and chunking for this document"
                        onClick={() => reindex(d)}
                      >
                        <Icon name="refresh" size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card
        icon="list"
        title="Review decision log"
        tight
        actions={
          <button
            type="button"
            className="btn btn-soft btn-sm"
            disabled={decided.length === 0}
            onClick={() => {
              exportCSV('kbl-review-decision-log.csv', [
                ['ID', 'Document', 'Decision', 'Reviewer', 'Created', 'Type'],
                ...decided.map((r) => [
                  r.id,
                  r.document_name,
                  r.status === 'approved' ? 'Approved' : 'Rejected',
                  r.decided_by ?? r.assigned_to ?? '',
                  r.created_at,
                  r.extraction_type ?? '',
                ]),
              ])
              toast('ok', 'Export complete', `kbl-review-decision-log.csv downloaded (${decided.length} rows).`)
            }}
          >
            <Icon name="dl" size={14} /> Export CSV
          </button>
        }
      >
        {decided.length === 0 ? (
          <EmptyState icon="list" title="No decisions yet" msg="Approvals and rejections appear here." />
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Document</th>
                  <th>Decision</th>
                  <th>Reviewer</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {decided.slice(0, 50).map((r) => {
                  const reviewer = r.decided_by ?? r.assigned_to
                  return (
                    <tr key={r.id}>
                      <td>
                        <strong>{r.document_name}</strong>
                      </td>
                      <td>
                        <span className={`pill ${r.status === 'rejected' ? 'p-danger' : 'p-ok'}`}>
                          {r.status === 'rejected' ? 'Rejected' : 'Approved'}
                        </span>
                      </td>
                      <td>
                        {reviewer ? <span title={reviewer}>{reviewerName(reviewer)}</span> : '—'}
                      </td>
                      <td>
                        <span className="tiny nowrap">{dateHuman(r.created_at)}</span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  )
}
